import React, { useState, useEffect } from 'react';
import {
Box,
Container,
Typography,
Paper,
TextField,
Button,
Rating,
Grid,
Chip,
Avatar,
Divider,
Alert,
Snackbar,
FormControl,
InputLabel,
Select,
useMediaQuery,
MenuItem,
useTheme,
Slide,
CircularProgress,
LinearProgress
} from '@mui/material';
import {
Feedback as FeedbackIcon,
Star as StarIcon,
StarBorder as StarBorderIcon,
Send as SendIcon,
Person as PersonIcon,
Email as EmailIcon,
CheckCircle as CheckCircleIcon,
Info as InfoIcon
} from '@mui/icons-material';
import NavigationMenu from '../navigation/NavigationMenu';
import useCustomToast from '../../hooks/useToast';
import axios from 'axios';
import { headers } from '../../utilities/Env';
import { APISendFeedback } from '../../utilities/APIS';

const FeedbackAndSuggestions = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showToast = useCustomToast();
    const [sending, setSending] = useState(false);
    const [userData, setUserData] = useState(null);
    
    const [feedback, setFeedback] = useState({
        name: '',
        email: '',
        category: '',
        message: '',
        rating: 0
    });
    
    const [showSuccess, setShowSuccess] = useState(false);
    const [featureRatings, setFeatureRatings] = useState({
        userInterface: 0,
        contentQuality: 0,
        performance: 0,
        support: 0
    });

    // Load user data from sessionStorage
    useEffect(() => {
        const user = sessionStorage.getItem('reseacher');
        console.log('Session user data:', user); // Debug log
        
        if (user) {
            const parsedUser = JSON.parse(user);
            setUserData(parsedUser);
            
            // Get name from session storage (using 'name' field as shown in your session storage structure)
            const userName = parsedUser.name || parsedUser.full_name || '';
            const userEmail = parsedUser.email || '';
            
            console.log('Extracted user name:', userName); // Debug log
            console.log('Extracted user email:', userEmail); // Debug log
            
            setFeedback(prev => ({
                ...prev,
                name: userName,
                email: userEmail
            }));
        } else {
            console.log('No user data found in sessionStorage');
        }
    }, []);

    const feedbackCategories = [
        'General Feedback',
        'Technical Issues',
        'Content Quality',
        'User Experience',
        'Bug Report',
        'Feature Request',
        'Suggestion',
        'Compliment',
        'Other'
    ];

    const handleFeedbackChange = (field, value) => {
        setFeedback(prev => ({ ...prev, [field]: value }));
    };

    const handleFeatureRating = (feature, value) => {
        setFeatureRatings(prev => ({ ...prev, [feature]: value }));
    };

    const calculateAverageRating = () => {
        const ratings = Object.values(featureRatings);
        const sum = ratings.reduce((a, b) => a + b, 0);
        return (sum / ratings.length).toFixed(1);
    };

    const sendFeedbackToBackend = async () => {
        // Validate required fields before sending
        if (!feedback.category) {
            showToast("Warning", "Please select a category", "warning");
            return false;
        }
        
        if (!feedback.message || feedback.message.trim().length < 10) {
            showToast("Warning", "Please provide detailed feedback (minimum 10 characters)", "warning");
            return false;
        }

        try {
            // Get the sender name from sessionStorage directly
            const storedUser = sessionStorage.getItem('reseacher');
            let senderName = '';
            let senderEmail = '';
            
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                // Try different possible field names for the name
                senderName = parsedUser.name || parsedUser.full_name || parsedUser.fullName || '';
                senderEmail = parsedUser.email || '';
            }
            
            // Use the provided name/email or fall back to session data
            const finalName = feedback.name || senderName || 'Anonymous User';
            const finalEmail = feedback.email || senderEmail || 'anonymous@ria.org';
            
            console.log('Sending feedback with name:', finalName); // Debug log
            console.log('Sending feedback with email:', finalEmail); // Debug log
            
            const payload = {
                name: finalName,
                email: finalEmail,
                category: feedback.category,
                message: feedback.message.trim(),
                rating: feedback.rating || 0,
                featureRatings: featureRatings,
                userData: {
                    id: userData?.id || null,
                    university: userData?.university || null,
                    level: userData?.level || null,
                    title: userData?.title || null
                },
                submissionDate: new Date().toLocaleString()
            };

            console.log('Sending payload:', payload); // Debug log

            const response = await axios.post(APISendFeedback, payload, { headers });

            if (response.data.status === 'success') {
                return true;
            } else {
                throw new Error(response.data.message || 'Failed to send feedback');
            }
        } catch (error) {
            console.error('API Error:', error);
            if (error.response) {
                console.error('Error response data:', error.response.data);
                const errorMsg = error.response.data?.message || 'Server error occurred';
                const validationErrors = error.response.data?.errors;
                
                if (validationErrors) {
                    const errorList = Object.values(validationErrors).flat().join(', ');
                    showToast("Validation Error", errorList, "error");
                } else {
                    showToast("Error", errorMsg, "error");
                }
            } else if (error.request) {
                showToast("Error", "Network error. Please check your connection.", "error");
            } else {
                showToast("Error", error.message || "Failed to send feedback", "error");
            }
            return false;
        }
    };

    const submitFeedback = async () => {
        if (!canSubmitFeedback) {
            showToast("Warning", "Please fill in all required fields (Category and Message)", "warning");
            return;
        }

        setSending(true);

        const success = await sendFeedbackToBackend();
        
        if (success) {
            setFeedback({
                name: userData?.name || userData?.full_name || '',
                email: userData?.email || '',
                category: '',
                message: '',
                rating: 0
            });
            setFeatureRatings({
                userInterface: 0,
                contentQuality: 0,
                performance: 0,
                support: 0
            });
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 6000);
            showToast("Success", "Your feedback has been sent successfully! We'll review it soon.", "success");
        }
        
        setSending(false);
    };

    const canSubmitFeedback = feedback.message.trim() && feedback.category;

    const getCompletionPercentage = () => {
        let filled = 0;
        let total = 5;
        
        if (feedback.name) filled++;
        if (feedback.email) filled++;
        if (feedback.category) filled++;
        if (feedback.message) filled++;
        if (feedback.rating > 0) filled++;
        
        return (filled / total) * 100;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            <NavigationMenu />
            <Container maxWidth="lg" sx={{ py: 4, flex: 1, marginTop: 8 }}>
                {/* Header Section */}
                <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                    <Box sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2
                    }}>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Feedback Form
                            </Typography>
                            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                Help us improve the Research and Innovations Academy by sharing your feedback
                            </Typography>
                        </Box>
                        {userData && (
                            <Chip
                                avatar={<Avatar>{userData.name?.charAt(0) || userData.full_name?.charAt(0)}</Avatar>}
                                label={`Welcome, ${userData.name || userData.full_name}`}
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                            />
                        )}
                    </Box>
                </Slide>

                {/* Progress Indicator */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Form Completion Progress
                        </Typography>
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                            {Math.round(getCompletionPercentage())}%
                        </Typography>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={getCompletionPercentage()} 
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                </Box>

                {/* Feedback Form */}
                <Grid container spacing={4}>
                    <Grid item xs={12} md={7}>
                        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                            <Box display="flex" alignItems="center" mb={3}>
                                <FeedbackIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                                <Typography variant="h5" fontWeight="bold">
                                    Share Your Feedback
                                </Typography>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Your Name"
                                        value={feedback.name}
                                        onChange={(e) => handleFeedbackChange('name', e.target.value)}
                                        variant="outlined"
                                        disabled={!!userData}
                                        InputProps={{
                                            readOnly: !!userData,
                                            startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                        helperText={userData ? "Auto-filled from your profile" : "Enter your name"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        type="email"
                                        value={feedback.email}
                                        onChange={(e) => handleFeedbackChange('email', e.target.value)}
                                        variant="outlined"
                                        disabled={!!userData}
                                        InputProps={{
                                            readOnly: !!userData,
                                            startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                        helperText={userData ? "Auto-filled from your profile" : "Enter your email"}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined" required>
                                        <InputLabel>Category *</InputLabel>
                                        <Select
                                            value={feedback.category}
                                            onChange={(e) => handleFeedbackChange('category', e.target.value)}
                                            label="Category *"
                                        >
                                            {feedbackCategories.map((category) => (
                                                <MenuItem key={category} value={category}>
                                                    {category}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={5}
                                        label="Your Feedback *"
                                        value={feedback.message}
                                        onChange={(e) => handleFeedbackChange('message', e.target.value)}
                                        variant="outlined"
                                        placeholder="Please share your detailed feedback about the platform (minimum 10 characters)..."
                                        required
                                        helperText={`${feedback.message.length}/10 characters minimum`}
                                        error={feedback.message.length > 0 && feedback.message.length < 10}
                                    />
                                </Grid>
                            </Grid>

                            <Button
                                variant="contained"
                                size="large"
                                endIcon={sending ? <CircularProgress size={24} /> : <SendIcon />}
                                onClick={submitFeedback}
                                disabled={!canSubmitFeedback || sending}
                                sx={{ 
                                    mt: 3,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                                fullWidth
                            >
                                {sending ? 'Sending...' : 'Submit Feedback'}
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Rating System */}
                    <Grid item xs={12} md={5}>
                        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                            <Box display="flex" alignItems="center" mb={3}>
                                <StarIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                                <Typography variant="h5" fontWeight="bold">
                                    Rate Our Platform
                                </Typography>
                            </Box>

                            <Box mb={4}>
                                <Typography variant="h6" gutterBottom textAlign="center" color="primary.main">
                                    Overall Experience
                                </Typography>
                                <Box display="flex" justifyContent="center" mb={2}>
                                    <Rating
                                        value={feedback.rating}
                                        onChange={(event, newValue) => handleFeedbackChange('rating', newValue)}
                                        size="large"
                                        icon={<StarIcon fontSize="inherit" />}
                                        emptyIcon={<StarBorderIcon fontSize="inherit" />}
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    {feedback.rating ? `${feedback.rating} out of 5 stars` : 'Click to rate your overall experience'}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" gutterBottom>
                                Rate Specific Features
                            </Typography>
                            
                            {[
                                { label: 'User Interface', key: 'userInterface', icon: '🎨', description: 'Ease of navigation and visual design' },
                                { label: 'Content Quality', key: 'contentQuality', icon: '📚', description: 'Quality and relevance of research content' },
                                { label: 'Performance', key: 'performance', icon: '⚡', description: 'Speed and reliability of the platform' },
                                { label: 'Support', key: 'support', icon: '🤝', description: 'Responsiveness and helpfulness of support team' }
                            ].map((feature) => (
                                <Box key={feature.key} mb={3}>
                                    <Typography variant="body2" gutterBottom fontWeight="bold">
                                        {feature.icon} {feature.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        {feature.description}
                                    </Typography>
                                    <Rating
                                        value={featureRatings[feature.key]}
                                        onChange={(event, newValue) => handleFeatureRating(feature.key, newValue)}
                                        size="medium"
                                    />
                                </Box>
                            ))}

                            {Object.values(featureRatings).some(r => r > 0) && (
                                <>
                                    <Divider sx={{ my: 3 }} />
                                    <Box bgcolor="primary.light" p={2} borderRadius={2}>
                                        <Typography variant="body2" color="primary.contrastText" align="center">
                                            <strong>Average Rating: {calculateAverageRating()}/5 stars</strong>
                                        </Typography>
                                    </Box>
                                </>
                            )}

                            <Divider sx={{ my: 3 }} />

                            <Alert severity="info" icon={<EmailIcon />}>
                                <strong>How your feedback is sent:</strong>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    • Securely sent to our server via API<br />
                                    • Includes all ratings and comments<br />
                                    • Your identity is kept confidential<br />
                                    • Expect a response within 2-3 business days
                                </Typography>
                            </Alert>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Success Message */}
                <Snackbar
                    open={showSuccess}
                    autoHideDuration={6000}
                    onClose={() => setShowSuccess(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={() => setShowSuccess(false)} 
                        severity="success" 
                        sx={{ width: '100%' }}
                        icon={<CheckCircleIcon fontSize="inherit" />}
                    >
                        <Typography variant="subtitle2" fontWeight="bold">
                            Thank You for Your Feedback!
                        </Typography>
                        <Typography variant="body2">
                            Your feedback has been sent successfully. We appreciate your contribution to improving RIA.
                        </Typography>
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default FeedbackAndSuggestions;


